import { useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { extractYouTubeId, youtubeEmbed } from '@/lib/youtube';

export interface VideoItem {
  id: string;
  title: string;
  description?: string | null;
  source_type: 'upload' | 'youtube';
  video_url: string;
}

interface VideoLightboxProps {
  videos: VideoItem[];
  currentIndex: number;
  isOpen: boolean;
  onClose: () => void;
  onNext: () => void;
  onPrev: () => void;
}

const VideoLightbox = ({ videos, currentIndex, isOpen, onClose, onNext, onPrev }: VideoLightboxProps) => {
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
    if (e.key === 'ArrowRight') onNext();
    if (e.key === 'ArrowLeft') onPrev();
  }, [onClose, onNext, onPrev]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  const current = videos[currentIndex];
  if (!current && isOpen) return null;

  const renderPlayer = () => {
    if (!current) return null;
    if (current.source_type === 'youtube') {
      const ytId = extractYouTubeId(current.video_url);
      if (!ytId) return <p className="text-white">Neplatné YouTube URL</p>;
      return (
        <iframe
          src={youtubeEmbed(ytId)}
          title={current.title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="absolute inset-0 w-full h-full rounded-sm"
        />
      );
    }
    return (
      <video
        key={current.id}
        src={current.video_url}
        controls
        autoPlay
        playsInline
        className="absolute inset-0 w-full h-full object-contain rounded-sm bg-black"
      />
    );
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 p-4"
          onClick={onClose}
        >
          <button onClick={onClose} className="absolute top-4 right-4 text-white/70 hover:text-white z-50 p-2">
            <X size={32} />
          </button>

          {videos.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); onPrev(); }}
                className="absolute left-4 text-white/70 hover:text-white z-50 p-2"
              >
                <ChevronLeft size={40} />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onNext(); }}
                className="absolute right-4 text-white/70 hover:text-white z-50 p-2"
              >
                <ChevronRight size={40} />
              </button>
            </>
          )}

          <motion.div
            key={currentIndex}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.3 }}
            className="flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="relative bg-black rounded-sm overflow-hidden"
              style={{ height: 'min(85vh, calc((100vw - 2rem) * 16 / 9))', aspectRatio: '9 / 16' }}
            >
              {renderPlayer()}
            </div>
            {current?.title && (
              <p className="mt-4 text-white text-lg tracking-wider" style={{ fontFamily: 'Bebas Neue' }}>
                {current.title}
              </p>
            )}
            {current?.description && (
              <p className="text-white/60 text-sm mt-1 text-center max-w-2xl">{current.description}</p>
            )}
            <p className="text-white/40 text-xs mt-2">{currentIndex + 1} / {videos.length}</p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default VideoLightbox;
